/* Gallery entries that are compositions, not one export (Fase D, 2026-10-24).
 *
 * Ported from the review bench (`reference-demos.jsx`, `ui-catalog.jsx`): the
 * KPI summary composition, the motion of one report page and the operator
 * page grammar (packages/admin-ui). Neutral sample data (D13). Typed by
 * `extra-demos.d.ts`.
 */
import { useEffect, useRef, useState } from "react";
import * as UI from "@omnitok/ui";
import * as BI from "@pervasivemind/bi-components";
import {
  CollapsibleFilterHeader,
  DashboardTabs,
  FadeSwap,
  RefreshOverlay,
} from "@pervasivemind/dashboard-render";
import {
  Callout as AdminCallout,
  ColorField,
  DataTable as AdminTable,
  DateCell,
  Disclosure,
  EmptyState,
  FormField,
  OnOffChip,
  PageHeader,
  Section,
  SectionStack,
  StatusChip,
} from "@pervasivemind/admin-ui";
import { Info, ListChecks, Plus, RotateCcw } from "lucide-react";

const fmt = (v) => v.toLocaleString("es-CL", { maximumFractionDigits: 1 });
const stores = ["Tienda A", "Tienda B", "Tienda C", "Tienda D", "Tienda E", "Tienda F"];
const rows = stores
  .map((retailer, i) => ({
    retailer,
    publicaciones: [210, 160, 240, 180, 98, 120][i],
    disponibles: [190, 150, 210, 155, 88, 104][i],
  }))
  .map((r) => ({ ...r, disponibilidad: +((100 * r.disponibles) / r.publicaciones).toFixed(1) }));
const columns = [
  { key: "retailer", label: "Tienda" },
  { key: "publicaciones", label: "Publicaciones" },
  { key: "disponibles", label: "Disponibles" },
  { key: "disponibilidad", label: "Disponibilidad", format: (v) => `${fmt(v)} %` },
];

function KpiComposition() {
  const [detail, setDetail] = useState(null);
  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3.5 lg:grid-cols-3">
        <BI.KPICore title="Disponibilidad" current={89} previous={86.7} previousLabel="Período anterior" format="percent" locale="es-CL" color={BI.PALETTE[4]} badge="" subtitle="897 de 1.008 publicaciones" entrance={false} action={{ label: "Explorar tiendas →", onClick: () => setDetail("Tiendas") }} />
        <BI.KPICard title="Publicaciones sin stock" value="111" delta={-16} deltaLabel="−16 vs. período anterior" sentiment="good" subtitle="11 % del surtido monitoreado" action={{ label: "Ver publicaciones →", onClick: () => setDetail("Publicaciones") }} />
        <BI.KPISparkline title="Cobertura de tiendas" value="6 de 6" sparkData={[4, 4, 5, 5, 6, 6, 6]} color={BI.PALETTE[0]} />
      </div>
      {detail && (
        <BI.Callout accent="info" title={detail} icon={<Info size={15} />} action={<button type="button" onClick={() => setDetail(null)}>Cerrar detalle</button>}>
          Vista de ejemplo seleccionada desde el KPI. La acción lleva al nivel de detalle del indicador.
        </BI.Callout>
      )}
    </div>
  );
}

const filterDefs = [
  { key: "brand", column: "brand", label: { es: "Marca" }, type: "select", default_value: "Marca A" },
  { key: "retailer", column: "retailer", label: { es: "Tienda" }, type: "select", default_value: "" },
];

function ReportMotion() {
  const [replay, setReplay] = useState(0);
  const [tab, setTab] = useState("summary");
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);
  const [filters, setFilters] = useState({ brand: "Marca A", retailer: "" });
  const timer = useRef(null);
  const reduced = BI.usePrefersReducedMotion();
  useEffect(() => () => clearTimeout(timer.current), []);
  function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    timer.current = setTimeout(() => {
      setVersion((v) => v + 1);
      setRefreshing(false);
    }, 850);
  }
  const sectionStyle = (i) =>
    reduced ? undefined : { animation: `pmRiseIn ${BI.MOTION.durBase}ms ${BI.MOTION.easeOut} ${i * 25}ms both` };
  const chartData = rows.map((r, i) => ({ label: r.retailer, value: version % 2 ? r.disponibilidad : Math.max(0, r.disponibilidad - 2 - i) }));
  BI.ensureMotionStyles();
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <UI.Button size="sm" variant="secondary" leftIcon={<RotateCcw size={13} />} onClick={() => setReplay((v) => v + 1)}>Reproducir entrada</UI.Button>
        <UI.Button size="sm" variant="secondary" onClick={refresh} disabled={refreshing}>{refreshing ? "Actualizando…" : "Simular actualización"}</UI.Button>
        <span className="ml-auto text-2xs text-neutral-500">{reduced ? "Movimiento reducido" : "Entrada ≤ 280 ms · respuesta 120–280 ms"}</span>
      </div>
      <div key={replay} data-motion-page="" aria-busy={refreshing} className="flex flex-col gap-[18px] overflow-hidden rounded-[14px] border border-neutral-200 bg-canvas p-6">
        <div style={sectionStyle(0)}>
          <h3 className="font-display text-lg font-extrabold text-neutral-900">El reporte se mueve como un conjunto.</h3>
          <p className="text-xs text-neutral-500">Prueba los filtros, las pestañas y la actualización.</p>
        </div>
        <div style={sectionStyle(1)} className="rounded-[14px] border border-neutral-200 bg-white p-3.5">
          <CollapsibleFilterHeader
            filters={filterDefs}
            activeFilters={filters}
            filterOptions={{ brand: ["Marca A", "Marca B"], retailer: stores }}
            onFilterChange={(key, value) => setFilters((p) => ({ ...p, [key]: value }))}
            onClear={() => setFilters({ brand: "Marca A", retailer: "" })}
            onResetFilter={(f) => setFilters((p) => ({ ...p, [f.key]: f.default_value || "" }))}
            today={new Date("2026-10-20")}
            surface="light"
          />
        </div>
        <div style={sectionStyle(2)}>
          <DashboardTabs tabs={[{ id: "summary", label: { es: "Resumen" }, section_ids: [] }, { id: "retailers", label: { es: "Tiendas" }, section_ids: [] }]} activeId={tab} onChange={setTab} />
        </div>
        <RefreshOverlay refreshing={refreshing}>
          <FadeSwap swapKey={tab}>
            <div style={sectionStyle(3)}>
              {tab === "summary" ? (
                <div className="flex flex-col gap-3.5">
                  <BI.SuppressEntrance><KpiComposition /></BI.SuppressEntrance>
                  <BI.ChartCard title="Disponibilidad por tienda" subtitle="Los datos anteriores permanecen visibles durante la actualización">
                    <FadeSwap swapKey={version}>
                      <BI.HorizontalBarT data={chartData} showShare={false} format={(v) => `${fmt(v)} %`} color={BI.PALETTE[4]} height={220} entrance={false} />
                    </FadeSwap>
                  </BI.ChartCard>
                </div>
              ) : (
                <BI.ComparisonTable title="Disponibilidad por tienda" rows={rows} columns={columns} compareKeys={[]} pageSize={5} />
              )}
            </div>
          </FadeSwap>
        </RefreshOverlay>
      </div>
      <div className="flex flex-wrap gap-4 text-2xs text-neutral-500">
        <span><b className="text-neutral-700">Entrada</b> Encabezado → filtros → contenido</span>
        <span><b className="text-neutral-700">Interacción</b> Hover, selección y despliegue</span>
        <span><b className="text-neutral-700">Actualización</b> Mantener contexto → fundir el dato</span>
      </div>
    </div>
  );
}

const adminRows = [
  { id: "disp", name: "Disponibilidad · Marca A", state: "Publicado", tone: "success", updated: "2026-10-20T14:05:00Z" },
  { id: "price", name: "Pricing · Marca A", state: "Borrador", tone: "neutral", updated: "2026-10-18T09:30:00Z" },
  { id: "share-search", name: "Share of Search · Marca B", state: "Recargando", tone: "running", updated: "2026-10-24T11:12:00Z" },
  { id: "exp", name: "Explorer · Marca C", state: "Falló la recarga", tone: "danger", updated: "2026-10-23T22:40:00Z" },
];

function OperatorPage() {
  const [color, setColor] = useState(BI.PALETTE[0]);
  return (
    <div className="flex flex-col gap-3.5">
      <PageHeader title="Reportes del proyecto" subtitle="Publica, recarga y revisa el estado de cada reporte." actions={<UI.Button variant="primary" leftIcon={<Plus size={14} />}>Nuevo reporte</UI.Button>} />
      <SectionStack>
        <Section title="Reportes" description="Cuatro reportes de ejemplo, con datos neutros." flush>
          <AdminTable
            rowKey={(r) => r.id}
            columns={[
              { id: "name", header: "Reporte", cell: (r) => r.name },
              { id: "state", header: "Estado", cell: (r) => <StatusChip label={r.state} tone={r.tone} /> },
              { id: "updated", header: "Actualizado", cell: (r) => <DateCell value={r.updated} locale="es" /> },
              { id: "auto", header: "Recarga automática", cell: (r) => <OnOffChip on={r.tone !== "neutral"} onLabel="Activa" offLabel="Apagada" /> },
            ]}
            rows={adminRows}
            actions={[{ id: "open", label: "Abrir", onSelect: () => {} }, { id: "delete", label: "Borrar", danger: true, onSelect: () => {} }]}
          />
        </Section>
        <Section title="Ajustes">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Nombre visible" help="Se muestra en el portal y en los correos." required>
              <UI.Input defaultValue="Disponibilidad" />
            </FormField>
            <FormField label="Clave del reporte" error="Ya existe un reporte con esta clave.">
              <UI.Input defaultValue="availability" error=" " />
            </FormField>
            <ColorField label="Color de la marca" value={color} onChange={setColor} help="Tiñe el sidebar y la selección del portal." />
          </div>
          <div className="mt-4 space-y-3">
            <AdminCallout tone="info" title="Cómo se publica">Los cambios se ven en el portal después de publicar.</AdminCallout>
            <AdminCallout tone="warning" title="Recarga pendiente">El parquet cambió y la última recarga es de ayer.</AdminCallout>
            <AdminCallout tone="danger" title="La recarga falló">No se pudo leer el archivo de la conexión.</AdminCallout>
            <Disclosure summary="Opciones avanzadas"><p className="text-xs text-neutral-600">Zona horaria y ventana de recarga.</p></Disclosure>
          </div>
        </Section>
        <Section title="Sin envíos">
          <EmptyState icon={<ListChecks size={22} />} title="Todavía no hay envíos" description="Programa el primero para recibir este reporte por correo." action={<UI.Button variant="secondary">Programar envío</UI.Button>} />
        </Section>
      </SectionStack>
    </div>
  );
}

export const EXTRA_ENTRIES = [
  {
    id: "kpi-composition",
    category: "compositions",
    componentName: "KPICore",
    covers: ["KPICore", "KPICard", "KPISparkline"],
    composition: ["KPICore", "KPICard", "KPISparkline", "Callout"],
    title: "Resumen de indicadores",
    subtitle: "Indicador principal, comparación y contexto, sin repetir señales visuales.",
    source: "bi",
    render: () => <KpiComposition />,
  },
  {
    id: "page-motion",
    category: "motion-page",
    componentName: "FadeSwap",
    covers: ["FadeSwap", "RefreshOverlay"],
    composition: ["CollapsibleFilterHeader", "DashboardTabs", "RefreshOverlay", "FadeSwap"],
    title: "Transiciones de una página",
    subtitle: "Entrada, interacción y actualización de un reporte completo, con datos de ejemplo.",
    source: "render",
    render: () => <ReportMotion />,
  },
  {
    id: "operator-page",
    category: "layout",
    componentName: "PageHeader",
    covers: ["PageHeader", "Section", "SectionStack", "DataTable", "RowActions", "StatusChip", "OnOffChip", "Callout", "EmptyState", "FormField", "ColorField", "Disclosure", "DateCell"],
    title: "Página de operador (admin-ui)",
    subtitle: "Builder y consola: cabecera, secciones, tabla con acciones, estados, avisos y formulario.",
    source: "admin",
    render: () => <OperatorPage />,
  },
];
